import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';
import { REQUEST } from '@core/models/requests';
import { API_ENDPOINTS } from '@base/constants/api-endpoints';
import { JubliaAIRecentPromptsResponse, JubliaAISearchResponse } from '@base/models/responses/jublia-ai.res';
import { Observable } from 'rxjs';
import { JubliaAISearchRequest } from '@base/models/requests/jublia-ai.req';
import { GlobalStateService } from './global-state.service';
import { JUBLIA_AI_CHATROOM_ID, JubliaAIRedirectEnum } from '@base/enums/jublia-ai.enum';
import { JubliaAIRedirection } from '@base/constants/menu-config';
import { ChatComponent } from '@navbar/chat/chat.component';
import { MODAL_SERVICE } from '@base/services/private/modal-service.token';

@Injectable({
  providedIn: 'root',
})
export class JubliaAiService {
  private readonly apiSrv = inject(ApiService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly modalSrv = inject(MODAL_SERVICE);

  private readonly targetListRead = signal<JubliaAIRedirectEnum[]>([]);

  readonly allowBroadSearch = computed(() => this.targetListRead().length > 0);
  readonly targetList = computed(() => this.targetListRead());

  readonly broadSearchAction = computed<
    { action: 'chat' } | { action: 'redirect'; target: JubliaAIRedirectEnum } | null
  >(() => {
    const targets = this.targetListRead();
    if (targets.length === 0) return null;
    if (targets.includes(JubliaAIRedirectEnum.CHAT) || targets.length > 1) {
      return { action: 'chat' };
    }
    return { action: 'redirect', target: targets[0] };
  });

  readonly broadSearchEntityKey = computed<JubliaAIRedirectEnum | null>(() => {
    const action = this.broadSearchAction();
    if (!action || action.action !== 'redirect') return null;
    return action.target;
  });

  /**
   * Set Target List
   * @param value - list target
   */
  setTargetList(value: JubliaAIRedirectEnum[]) {
    this.targetListRead.set(value);
  }

  /**
   * Sends a search request to the Jublia AI search endpoint.
   * @param payload - The search request payload.
   * @returns - An Observable emitting the search response.
   */
  postJubliaAISearch(payload: JubliaAISearchRequest): Observable<JubliaAISearchResponse> {
    const PAYLOAD: REQUEST = {
      api: API_ENDPOINTS.JUBLIA_AI.SEARCH,
      data: payload,
    };
    return this.apiSrv.post<JubliaAISearchResponse>(PAYLOAD);
  }

  /**
   * Fetches the list of recent prompts used in Jublia AI.
   * @returns - An Observable emitting an array of recent prompt strings.
   */
  getRecentPrompts(): Observable<JubliaAIRecentPromptsResponse> {
    const PAYLOAD: REQUEST = {
      api: API_ENDPOINTS.JUBLIA_AI.RECENT_PROMPTS,
    };
    return this.apiSrv.get<JubliaAIRecentPromptsResponse>(PAYLOAD);
  }

  /**
   * checking availability of Entity
   */
  checkEntityAvailability(target: JubliaAIRedirectEnum): boolean {
    const isHasAgenda = this.globalStateSrv.isHasAgenda();
    const isHasProduct = this.globalStateSrv.isHasProduct();
    const isHasCompany = this.globalStateSrv.isHasCompany();
    const isAnon = this.globalStateSrv.isAnon();

    if (target === JubliaAIRedirectEnum.AGENDA) {
      return isHasAgenda;
    } else if (target === JubliaAIRedirectEnum.COMPANY) {
      return isHasCompany;
    } else if (target === JubliaAIRedirectEnum.PRODUCT) {
      return isHasProduct;
    } else if (target === JubliaAIRedirectEnum.SPEAKER) {
      return isHasAgenda;
    } else if (target === JubliaAIRedirectEnum.CHAT) {
      return true;
    } else {
      if (isAnon) {
        return false;
      }

      return true;
    }
  }

  /**
   * Performs a board search across all entities
   * @param searchId - The search ID
   * @returns Promise containing search results with target navigation information
   */
  broadenSearch(searchId: string): Observable<JubliaAISearchResponse> {
    const PAYLOAD: REQUEST = {
      api: API_ENDPOINTS.JUBLIA_AI.BROADEN_SEARCH,
      data: { search_id: searchId },
    };
    return this.apiSrv.post<JubliaAISearchResponse>(PAYLOAD);
  }

  /**
   * Gets the search ID for the current search
   * @returns Promise containing the search ID
   */
  getSearchId(): Observable<{ search_id: string }> {
    const PAYLOAD: REQUEST = {
      api: API_ENDPOINTS.JUBLIA_AI.INIT_SEARCH,
    };
    return this.apiSrv.post<{ search_id: string }>(PAYLOAD);
  }

  /**
   * Gets the AI overview for the current search
   * @param searchId - The search ID
   * @returns Promise containing the AI overview
   */
  getAIOverview(
    searchId: string,
    page: JubliaAIRedirectEnum
  ): Observable<{ overview: string; allow_broad_search: boolean; target_list: JubliaAIRedirectEnum[] }> {
    const PAYLOAD: REQUEST = {
      api: API_ENDPOINTS.JUBLIA_AI.AI_OVERVIEW,
      data: {
        page: page === JubliaAIRedirectEnum.ONDEMAND ? JubliaAIRedirectEnum.AGENDA : page,
        search_id: searchId,
        is_on_demand: page === JubliaAIRedirectEnum.ONDEMAND,
      },
    };
    return this.apiSrv.post<{ overview: string; allow_broad_search: boolean; target_list: JubliaAIRedirectEnum[] }>(
      PAYLOAD
    );
  }

  /**
   * Determine the next action for broad search based on the target list.
   * @returns 'chat' if chat should be opened, 'redirect' if it should redirect, or null if no targets.
   */
  getBroadSearchAction(): { action: 'chat' } | { action: 'redirect'; target: JubliaAIRedirectEnum } | null {
    return this.broadSearchAction();
  }

  /**
   * Mapping the redirect after a broaden search
   * @param searchId - The search ID
   */
  handleRedirectAfterBroadenSearch(
    eventDirPath: string,
    broadenResult: JubliaAISearchResponse
  ): { navigate: string[]; state: { searchId: string; target: string } } {
    const navigate = JubliaAIRedirection(eventDirPath, broadenResult.target || [eventDirPath]);
    return { navigate, state: { searchId: broadenResult?.search_id || '', target: broadenResult?.target || '' } };
  }

  /**
   * Opens the `ChatComponent` as a fullscreen modal.
   *
   * Configures and displays using the modal service.
   *
   * @returns void
   */
  showChat(searchId?: string, promptValue?: string): void {
    const modalProps: Partial<ChatComponent> = {
      searchId,
      chatroomId: JUBLIA_AI_CHATROOM_ID,
      askAnythingMessage: promptValue,
    };
    this.modalSrv.showModalChat({ addsData: modalProps });
  }
}
